import { DsdFile } from "@/types/dsd";

import { useDsdCompareHistory } from "@/hooks/queries/use-dsd-compare-history";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { GitCompare, History } from "lucide-react";
import { useState } from "react";
import { DsdCardWrapper, DsdEmptyState, DsdInfo } from "./dsd-select";
import { HistorySidebar } from "./history-sidebar";

// ============================================
// DsdSelectionCard
// ============================================

export interface DsdSelectionCardProps {
  /** 카드 타입: 기준(base) 또는 비교 대상(compare) */
  type: "base" | "compare";
  /** 선택된 DSD 파일 (없으면 empty state 표시) */
  data?: DsdFile | null;
  /** 필터링 기준 파일 (상대편 파일이 선택된 경우) */
  filterByFile?: DsdFile | null;
  /** 파일 선택/변경 핸들러 */
  onFileSelect?: (file: DsdFile | null) => void;
  className?: string;
}

const CARD_CONFIG = {
  base: {
    number: 1 as const,
    title: "DSD 보고서",
    changeButtonText: "새로운 파일 선택하기",
  },
  compare: {
    number: 2 as const,
    title: "DSD 보고서",
    changeButtonText: "새로운 파일 선택하기",
  },
};

/** DSD 선택 카드 - 데이터 유무에 따라 정보 또는 빈 상태 표시 */
export const DsdSelectionCard = ({
  type,
  data,
  filterByFile,
  onFileSelect,
  className,
}: DsdSelectionCardProps) => {
  const config = CARD_CONFIG[type];
  const hasData = !!data;

  // 선택 초기화 → empty state로 되돌림
  const handleChangeClick = () => {
    onFileSelect?.(null);
  };

  return (
    <DsdCardWrapper
      number={config.number}
      title={config.title}
      className={cn("flex-1", className)}
    >
      {hasData ? (
        <div className="flex flex-col gap-3 w-full">
          <DsdInfo data={data} />
          <div className="flex justify-end">
            <Button variant="outlined" size={32} onClick={handleChangeClick}>
              {config.changeButtonText}
            </Button>
          </div>
        </div>
      ) : (
        <DsdEmptyState
          modalType={type}
          filterByFile={filterByFile}
          onFileSelect={onFileSelect}
        />
      )}
    </DsdCardWrapper>
  );
};

// ============================================
// DSDsCompare (두 카드 비교 영역)
// ============================================

export interface DSDsCompareProps {
  /** 기준 DSD 파일 */
  baseFile?: DsdFile | null;
  /** 비교 대상 DSD 파일 */
  compareFile?: DsdFile | null;
  /** 기준 DSD 파일 선택 핸들러 */
  onSelectBaseFile?: (file: DsdFile | null) => void;
  /** 비교 대상 DSD 파일 선택 핸들러 */
  onSelectCompareFile?: (file: DsdFile | null) => void;
}

/** DSD 비교 컴포넌트 - 두 DSD 파일을 나란히 비교 */
export const DSDsCompare = ({
  baseFile,
  compareFile,
  onSelectBaseFile,
  onSelectCompareFile,
}: DSDsCompareProps) => {
  return (
    <div className="flex gap-6 items-start justify-center w-full">
      <DsdSelectionCard
        type="base"
        data={baseFile}
        filterByFile={compareFile}
        onFileSelect={onSelectBaseFile}
      />
      <DsdSelectionCard
        type="compare"
        data={compareFile}
        filterByFile={baseFile}
        onFileSelect={onSelectCompareFile}
      />
    </div>
  );
};

// ============================================
// DsdComparePage (전체 페이지)
// ============================================

export interface DsdComparePageProps {
  /** 기준 DSD 파일 */
  baseFile?: DsdFile | null;
  /** 비교 대상 DSD 파일 */
  compareFile?: DsdFile | null;
  /** 기준 DSD 파일 선택 핸들러 */
  onSelectBaseFile?: (file: DsdFile | null) => void;
  /** 비교 대상 DSD 파일 선택 핸들러 */
  onSelectCompareFile?: (file: DsdFile | null) => void;
  /** 비교 시작 핸들러 */
  onCompare?: () => void;
  className?: string;
}

/** DSD 연금술사 페이지 전체 레이아웃 */
export const DsdComparePage = ({
  baseFile,
  compareFile,
  onSelectBaseFile,
  onSelectCompareFile,
  onCompare,
  className,
}: DsdComparePageProps) => {
  // 두 파일 모두 선택되었을 때만 비교 가능
  const canCompare = !!baseFile && !!compareFile;

  // 히스토리 데이터 - 페이지 진입 시 미리 호출
  const { items: historyItems, isLoading: isHistoryLoading } =
    useDsdCompareHistory();
  const hasHistory = historyItems.length > 0;

  // 히스토리 사이드바 상태
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);

  const handleHistoryClick = () => {
    setIsHistoryOpen((prev) => !prev);
  };

  const handleHistoryClose = () => {
    setIsHistoryOpen(false);
  };

  return (
    <div className={cn("flex h-full w-full", className)}>
      {/* 메인 콘텐츠 영역 */}
      <div className="flex-1 flex flex-col items-center overflow-auto bg-white py-32">
        <div className="flex flex-col gap-9 items-center w-full p-16 min-w-[784px]">
          {/* 헤더 섹션 */}
          <div className="flex flex-col gap-2 items-start max-w-[1144px] w-full">
            <div className="flex items-center gap-2">
              <h1 className="title-2-bold text-label-normal">
                DSD 보고서 비교
              </h1>
            </div>
            <p className="body-1-normal-medium text-label-neutral">
              같은 분기 내의 검토/감사 보고서를 대사하여 변경 내역과 단수 차이,
              합계 일치 여부를 확인하세요.
            </p>
          </div>

          {/* 버튼 + 카드 컨테이너 */}
          <div className="flex flex-col gap-5 items-center w-full">
            {/* 버튼 행 */}
            <div className="flex items-center justify-end gap-2 max-w-[1144px] w-full">
              <Button
                variant="outlined"
                size={36}
                leftIcon={<History size={16} />}
                onClick={handleHistoryClick}
                disabled={isHistoryLoading || !hasHistory}
              >
                비교 내역
              </Button>
              <Button
                variant="solid"
                color="secondary"
                size={36}
                leftIcon={<GitCompare size={18} />}
                disabled={!canCompare}
                onClick={onCompare}
              >
                DSD 보고서 비교
              </Button>
            </div>

            {/* 카드 행 */}
            <DSDsCompare
              baseFile={baseFile}
              compareFile={compareFile}
              onSelectBaseFile={onSelectBaseFile}
              onSelectCompareFile={onSelectCompareFile}
            />
          </div>
        </div>
      </div>

      {/* 히스토리 사이드바 */}
      <HistorySidebar isOpen={isHistoryOpen} onClose={handleHistoryClose} />
    </div>
  );
};
